/**
 * Prüft ob sich ein Feldwert gegenüber dem aktuellen Datenobjekt geändert hat.
 * Normalisiert null/undefined für korrekten Vergleich.
 */
export function hasFieldChanged<T extends object>(
  currentObject: T | null | undefined,
  field: string,
  newValue: unknown,
): boolean {
  const currentValue =
    (currentObject as Record<string, unknown>)?.[field] ?? null;
  const normalizedNew = newValue ?? null;
  return currentValue !== normalizedNew;
}

/**
 * Prüft ob sich eine Mengen-Zuordnung geändert hat (z.B. n:m-Verknüpfungen als ID-Liste).
 *
 * Eigene Funktion, weil `hasFieldChanged` mit `!==` vergleicht und für zwei inhaltsgleiche
 * Arrays deshalb immer "geändert" liefert. Die Reihenfolge zählt nicht – eine Zuordnung ist
 * eine Menge, keine Liste. Doppelte Einträge kommen in einer Zuordnung nicht vor.
 *
 * Erwartet auf beiden Seiten fertige IDs: das Auflösen aus den Objekten am Datensatz
 * (`vorhabenbestandteile` → `id`) gehört in den Hook der jeweiligen Domäne.
 */
export function hasListChanged(
  currentIds: string[],
  newIds: string[],
): boolean {
  if (currentIds.length !== newIds.length) return true;
  const vorhanden = new Set(currentIds);
  return newIds.some((id) => !vorhanden.has(id));
}

/**
 * Prüft ob sich mindestens ein Feld in einem Update-Objekt gegenüber dem
 * aktuellen Datenobjekt geändert hat. Für Item-Updates mit partiellen Daten.
 *
 * Nur für skalare Felder – Listenwerte gehören in `hasListChanged`.
 */
export function hasAnyFieldChanged<T extends object, U extends object>(
  currentObject: T | null | undefined,
  updates: U,
): boolean {
  if (!currentObject) return true;
  return Object.entries(updates).some(([key, value]) =>
    hasFieldChanged(currentObject, key, value),
  );
}
